import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../api";
import { Button } from "../components/ui/button";
import usePageTitle from "../hooks/usePageTitle";
import { CheckCircle2, XCircle, AlertTriangle, Clock } from "lucide-react";

/**
 * Public, login-free review screen reached from an email link:
 *   /review/:submissionId?action=approve&token=<...>
 *   /review/:submissionId?action=reject&token=<...>
 *
 * Flow:
 *   1. Fetch the submission via token. If token invalid / expired / used →
 *      render the appropriate error state.
 *   2. Show the summary + a single "Confirm approval" or "Confirm rejection"
 *      button (the latter requires a reason).
 *   3. On confirm → POST /portal/review/:id/confirm with the token. Show the
 *      final "Submission X approved/rejected" state.
 */
export default function Review() {
  usePageTitle("Review Submission");
  const { submissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const initialAction = params.get("action") === "reject" ? "reject" : "approve";
  const token = params.get("token") || "";

  const [submission, setSubmission] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(initialAction);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { status: "approved" | "rejected" }

  useEffect(() => {
    if (!token) {
      setError({ kind: "invalid", message: "This link is not recognised." });
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/portal/review/${submissionId}`, { params: { token } })
      .then((res) => setSubmission(res.data))
      .catch((err) => {
        const detail = err.response?.data?.detail || "This link is not recognised.";
        const status = err.response?.status;
        const kind =
          status === 410 ? "expired" :
          status === 409 ? "already" :
          "invalid";
        setError({ kind, message: detail });
      })
      .finally(() => setLoading(false));
  }, [submissionId, token]);

  const handleConfirm = async () => {
    if (action === "reject" && !reason.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/portal/review/${submissionId}/confirm`,
        { action, reason: action === "reject" ? reason.trim() : undefined },
        { params: { token } },
      );
      setDone({ status: res.data.status });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Action failed.";
      const kind =
        status === 410 ? "expired" :
        status === 409 ? "already" :
        "invalid";
      setError({ kind, message: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="review-page" style={pageWrap}>
      <div style={card}>
        <BrandHeader />
        {loading && <p style={{ color: "#9ca3af" }}>Loading submission…</p>}

        {!loading && error && <ErrorState error={error} onGoToApp={() => navigate("/portal-admin")} />}

        {!loading && !error && done && (
          <DoneState
            status={done.status}
            reference={submissionId}
            supplierName={submission?.supplier_name}
          />
        )}

        {!loading && !error && !done && submission && (
          <ConfirmForm
            submission={submission}
            action={action}
            setAction={setAction}
            reason={reason}
            setReason={setReason}
            submitting={submitting}
            onConfirm={handleConfirm}
          />
        )}
      </div>
    </div>
  );
}


function BrandHeader() {
  return (
    <div style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
      <i className="fas fa-chart-line" style={{ color: "#F050F8", fontSize: "1.25rem" }}></i>
      <span style={{ color: "#e5e7eb", fontWeight: 700, letterSpacing: "0.02em" }}>
        ProcOps · Review
      </span>
    </div>
  );
}


function ErrorState({ error, onGoToApp }) {
  const map = {
    expired: { icon: <Clock size={36} color="#f59e0b" />, title: "Link expired" },
    already: { icon: <AlertTriangle size={36} color="#60a5fa" />, title: "Already reviewed" },
    invalid: { icon: <XCircle size={36} color="#f43f5e" />, title: "Link not recognised" },
  };
  const cfg = map[error.kind] || map.invalid;
  return (
    <div data-testid={`review-error-${error.kind}`} style={{ textAlign: "center", padding: "1.5rem 0" }}>
      <div style={{ marginBottom: "1rem" }}>{cfg.icon}</div>
      <h2 style={{ color: "#e5e7eb", margin: "0 0 0.5rem", fontSize: "1.4rem" }}>{cfg.title}</h2>
      <p style={{ color: "#9ca3af", margin: 0, fontSize: "0.95rem", lineHeight: 1.6 }}>{error.message}</p>
      {error.kind !== "invalid" && (
        <Button onClick={onGoToApp} className="btn btn-secondary" style={{ marginTop: "1.5rem" }} data-testid="go-to-app-btn">
          Open ProcOps
        </Button>
      )}
    </div>
  );
}


function DoneState({ status, reference, supplierName }) {
  const isApproved = status === "approved";
  return (
    <div data-testid={`review-done-${status}`} style={{ textAlign: "center", padding: "1.5rem 0" }}>
      <div style={{ marginBottom: "1rem" }}>
        {isApproved
          ? <CheckCircle2 size={48} color="#22c55e" />
          : <XCircle size={48} color="#f43f5e" />}
      </div>
      <h2 style={{ color: "#e5e7eb", margin: "0 0 0.5rem", fontSize: "1.4rem" }}>
        Submission {reference.slice(0, 8)} {isApproved ? "approved" : "rejected"}.
      </h2>
      {supplierName && (
        <p style={{ color: "#9ca3af", margin: 0, fontSize: "0.95rem" }}>
          {supplierName} · You can close this tab.
        </p>
      )}
    </div>
  );
}


function ConfirmForm({ submission, action, setAction, reason, setReason, submitting, onConfirm }) {
  const isReject = action === "reject";
  return (
    <div data-testid="review-confirm-form">
      <h2 style={{ color: "#e5e7eb", margin: "0 0 0.25rem", fontSize: "1.3rem" }}>
        {isReject ? "Reject submission" : "Approve submission"}
      </h2>
      <p style={{ color: "#9ca3af", margin: "0 0 1.25rem", fontSize: "0.9rem" }}>
        Review the details below, then confirm to {isReject ? "reject" : "approve"} this price update.
      </p>

      <div style={summaryBox}>
        <Row label="Supplier" value={submission.supplier_name} />
        <Row label="Category" value={submission.supplier_category} />
        <Row label="SKUs" value={submission.sku_count} />
        <Row label="Reference" value={submission.id.slice(0, 8)} mono />
        <Row label="Type" value={submission.type === "manual" ? "Manual entry" : "File upload"} />
        <Row label="Submitted" value={new Date(submission.submitted_at).toLocaleString()} />
        {submission.notes && <Row label="Notes" value={submission.notes} />}
        {submission.original_filename && <Row label="File" value={submission.original_filename} />}
      </div>

      {submission.impact && (
        <div style={{ border: "1px solid #2a2f3a", borderRadius: 10, padding: "14px 16px", margin: "12px 0 2px", background: "rgba(255,255,255,0.02)" }} data-testid="review-impact">
          {submission.impact.modelled ? (() => {
            const imp = submission.impact;
            const ad = imp.annualDelta || 0;
            const col = ad > 0 ? "#fb7185" : ad < 0 ? "#34d399" : "#9ca3af";
            const verb = ad > 0 ? "adds" : ad < 0 ? "saves" : "makes no net change of";
            return (
              <>
                <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "#9ca3af", fontWeight: 700 }}>Modelled annual cost impact</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: col, marginTop: 4 }}>
                  £{Math.round(Math.abs(ad)).toLocaleString("en-GB")}<span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 600 }}> / yr</span>
                </div>
                <div style={{ fontSize: 12.5, color: "#9ca3af", marginTop: 6, lineHeight: 1.5 }}>
                  This update {verb} an estimated £{Math.round(Math.abs(ad)).toLocaleString("en-GB")} a year against the loaded BOM
                  {imp.deltaPct != null ? `, ${imp.deltaPct > 0 ? "+" : ""}${imp.deltaPct}% on cost per install` : ""}. {imp.matched} of {imp.priced} priced rows matched.
                </div>
                {imp.uomWarnings > 0 && (
                  <div style={{ fontSize: 12, color: "#fbbf24", marginTop: 6, fontWeight: 600 }}>
                    {imp.uomWarnings} line{imp.uomWarnings > 1 ? "s" : ""} look like a unit-of-measure mismatch and were excluded from this figure.
                  </div>
                )}
                {imp.newParts > 0 && (
                  <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6, fontWeight: 600 }}>
                    {imp.newParts} new product{imp.newParts > 1 ? "s" : ""} excluded — no prior price or usage, so the impact can't be modelled.
                  </div>
                )}
              </>
            );
          })() : (
            <div style={{ fontSize: 12.5, color: "#9ca3af" }}>
              Cost impact not modelled: {submission.impact.priced ? `${submission.impact.matched} of ${submission.impact.priced} priced rows matched the platform BOM.` : "no BOM loaded or no priced rows."}
            </div>
          )}
        </div>
      )}

      {/* Action switcher — let the reviewer flip from approve→reject (or vice versa)
          without re-opening their email. */}
      <div style={{ display: "flex", gap: "0.5rem", margin: "1rem 0 1rem", alignItems: "center" }}>
        <span style={{ color: "#9ca3af", fontSize: "0.85rem" }}>Action:</span>
        <button
          onClick={() => setAction("approve")}
          data-testid="action-toggle-approve"
          style={{ ...pill, ...(action === "approve" ? pillActiveOK : pillInactive) }}
        >Approve</button>
        <button
          onClick={() => setAction("reject")}
          data-testid="action-toggle-reject"
          style={{ ...pill, ...(action === "reject" ? pillActiveNo : pillInactive) }}
        >Reject</button>
      </div>

      {isReject && (
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ color: "#e5e7eb", fontSize: "0.85rem", display: "block", marginBottom: "0.35rem" }}>
            Reason for rejection (required)
          </label>
          <textarea
            data-testid="reject-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            style={textareaStyle}
            placeholder="Why are you rejecting this submission?"
          />
        </div>
      )}

      <Button
        onClick={onConfirm}
        disabled={submitting || (isReject && !reason.trim())}
        className={isReject ? "btn btn-danger" : "btn btn-primary"}
        data-testid="confirm-action-btn"
        style={{ width: "100%", padding: "0.75rem", fontSize: "0.95rem" }}
      >
        {submitting
          ? "Submitting…"
          : isReject ? "Confirm rejection" : "Confirm approval"}
      </Button>
    </div>
  );
}


function Row({ label, value, mono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "0.35rem 0", gap: "1rem" }}>
      <span style={{ color: "#9ca3af", fontSize: "0.85rem" }}>{label}</span>
      <span style={{ color: "#e5e7eb", fontSize: "0.9rem", fontFamily: mono ? "monospace" : "inherit", textAlign: "right" }}>{value}</span>
    </div>
  );
}


// ─── Styles (inline; this page has no Tailwind/shared CSS coverage) ─────────

const pageWrap = {
  minHeight: "100vh",
  background: "linear-gradient(135deg, #0A122A 0%, #050B1F 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2rem 1rem",
};

const card = {
  background: "rgba(10,18,42,0.95)",
  border: "1px solid rgba(96,240,248,0.18)",
  borderRadius: "12px",
  padding: "2rem",
  maxWidth: "520px",
  width: "100%",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
};

const summaryBox = {
  background: "rgba(96,240,248,0.05)",
  border: "1px solid rgba(96,240,248,0.15)",
  borderRadius: "8px",
  padding: "0.85rem 1rem",
};

const textareaStyle = {
  width: "100%",
  padding: "0.6rem 0.8rem",
  borderRadius: "6px",
  background: "rgba(10,18,42,0.6)",
  border: "1px solid rgba(96,240,248,0.2)",
  color: "#e5e7eb",
  fontSize: "0.9rem",
  fontFamily: "inherit",
  resize: "vertical",
};

const pill = {
  padding: "0.3rem 0.85rem",
  borderRadius: "999px",
  border: "1px solid transparent",
  cursor: "pointer",
  fontSize: "0.8rem",
  fontWeight: 600,
};
const pillInactive = { background: "transparent", borderColor: "rgba(255,255,255,0.15)", color: "#9ca3af" };
const pillActiveOK = { background: "rgba(34,197,94,0.18)", borderColor: "#22c55e", color: "#86efac" };
const pillActiveNo = { background: "rgba(244,63,94,0.18)", borderColor: "#f43f5e", color: "#fda4af" };
